import { getCountryCode } from "@/components/dropdown/MarketsDropdown/utils";

export const SPIRIT_DRINKERS_ICON = "/images/market/spirit_drinkers.png";

export interface MarketFlagEntity {
  name?: string | null;
  identity?: string | null;
  groupName?: string | null;
}

/**
 * Resolves a market entity to its circular flag image URL.
 *
 * Markets are identified by a non-empty `identity` (brands/brand sets carry
 * `logo`/`type` instead). Dispatch mirrors the legacy getIconForEntity market
 * branch so the point-in-time views, pickers, and the distributions view all
 * derive the same flag from the same entity:
 *  - Spirit Drinkers group  → local spirit-drinkers asset
 *  - `US…` identities       → US flag (custom US markets: Westchester, …)
 *  - otherwise              → getCountryCode(name, code, identity) → CDN SVG
 *
 * getCountryCode returns the sentinel "spiritDrinkers" for HSS/CTM-CA codes;
 * legacy code interpolated it into a broken flags/spiritDrinkers.svg URL —
 * here it maps to the local asset.
 *
 * Returns null when the entity is not a market or no flag can be resolved
 * (callers fall through to their existing placeholder/letter-chip branch).
 */
export function resolveMarketFlagUrl(
  entity: MarketFlagEntity | null | undefined,
): string | null {
  if (!entity?.identity) return null;
  if (entity.groupName === "Spirit Drinkers") return SPIRIT_DRINKERS_ICON;

  const identityCode = entity.identity.startsWith("US")
    ? "US"
    : entity.identity;
  const code = getCountryCode(
    entity.name ?? "",
    identityCode,
    entity.identity,
  );
  if (!code) return null;
  if (code === "spiritDrinkers") return SPIRIT_DRINKERS_ICON;
  return `https://hatscripts.github.io/circle-flags/flags/${code}.svg`;
}
